/**
 * Public landing sticky nav (issue #17) — the demo header in Tailwind + Folio
 * tokens. Branding (name/logo/parent_org) is DB-driven, passed in by the page.
 * "Sign in" is a real route; Programs/Request are in-page anchors.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowDownRight, ArrowRight, Menu, X } from "lucide-react";
import { Link } from "react-router";

import { PUBLIC_FOCUS, PUBLIC_LINK } from "@/components/public/control-classes";

import { BrandMark } from "@/components/BrandMark";
import { CtaLink } from "@/components/public/CtaLink";
import { cn } from "@/lib/utils";

interface LandingHeaderProps {
  brandName: string;
  logoUrl: string | null;
  parentOrg: string | null;
  navItems?: { href: string; label: string }[];
  ctaHref?: string;
  ctaLabel?: string;
  mobileCtaLabel?: string;
  editorial?: boolean;
  showSignIn?: boolean;
}

const DEFAULT_NAV_ITEMS = [
  { href: "#programs", label: "Programs" },
  { href: "#request", label: "Request a seat" },
];

export function LandingHeader({
  brandName,
  logoUrl,
  parentOrg,
  navItems = DEFAULT_NAV_ITEMS,
  ctaHref = "#request",
  ctaLabel = "Request a seat",
  mobileCtaLabel,
  editorial = false,
  showSignIn = true,
}: LandingHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuTrigger = useRef<HTMLButtonElement>(null);
  const hasNavigationItems = navItems.length > 0;
  const primaryCta = (
    <CtaLink
      to={ctaHref}
      className={cn(
        "px-3 text-meta",
        editorial
          ? "hidden sm:inline-flex sm:px-[14px]"
          : "sm:px-[18px]",
      )}
    >
      {mobileCtaLabel ? (
        <>
          <span className="sm:hidden">{mobileCtaLabel}</span>
          <span className="hidden sm:inline">{ctaLabel}</span>
        </>
      ) : (
        ctaLabel
      )}
    </CtaLink>
  );

  // While the drawer is open it is a layer, not page content: Escape dismisses
  // it and the page behind it must not scroll away under the panel.
  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const closeMenu = () => setMobileMenuOpen(false);

    // The panel is sm:hidden, so crossing that breakpoint (rotating a phone to
    // landscape) would strand the scroll lock with no visible control to
    // release it. Close instead.
    const desktopQuery =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(min-width: 640px)")
        : null;
    if (desktopQuery?.matches) {
      closeMenu();
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
        menuTrigger.current?.focus();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    desktopQuery?.addEventListener("change", closeMenu);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      desktopQuery?.removeEventListener("change", closeMenu);
    };
  }, [mobileMenuOpen]);

  return (
    <header
      className={cn(
        "sticky top-0 border-b border-[var(--public-rule)]/70",
        // Above the fixed apply bar (z-50) so the scrim covers it too.
        mobileMenuOpen ? "z-[60]" : "z-40",
        editorial
          ? "bg-[var(--public-background)]"
          : "bg-[var(--public-background)]/85 backdrop-blur-[18px] backdrop-saturate-150",
      )}
    >
      <div
        className={cn(
          "mx-auto flex w-full items-center justify-between gap-3",
          // The scrim is a negative-z child, so it paints over this header's
          // own background. Re-assert it here so only the page below dims.
          mobileMenuOpen && "relative bg-[var(--public-background)]",
          editorial
            ? "max-w-[1240px] px-5 py-[13px] sm:gap-8 sm:px-10 lg:px-12 lg:py-[15px]"
            : "max-w-[1180px] px-4 py-3 sm:gap-6 sm:px-10 sm:py-4",
        )}
      >
        <a
          href="#top"
          className={`${PUBLIC_LINK} flex min-w-0 items-center gap-2.5 sm:gap-3`}
        >
          <span aria-hidden="true" className="contents">
            {logoUrl ? (
              <BrandMark
                name={brandName}
                logoUrl={logoUrl}
                className={cn(
                  "shrink-0",
                  editorial ? "size-[38px] sm:size-10" : "size-9 sm:size-[42px]",
                )}
              />
            ) : (
              <span
                className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--public-surface)] sm:size-[42px]"
                style={{
                  boxShadow:
                    "inset 0 0 0 1px color-mix(in oklab, var(--public-highlight-deep) 70%, transparent), inset 0 0 0 4px var(--public-surface), inset 0 0 0 5px color-mix(in oklab, var(--public-highlight-deep) 32%, transparent)",
                }}
              >
                <BrandMark
                  name={brandName}
                  logoUrl={null}
                  className="size-6 text-[10px] text-[var(--public-primary)]"
                />
              </span>
            )}
          </span>
          <span
            className={cn(
              "min-w-0 font-display font-semibold leading-none text-[var(--public-foreground)]",
              editorial
                ? "text-[18px] tracking-[0.005em] sm:text-[21px]"
                : "text-[17px] tracking-[0.01em] sm:text-[21px]",
            )}
          >
            {brandName}
            {" "}
            {parentOrg && (
              <small
                className={cn(
                  "mt-[6px] hidden font-mono font-medium uppercase text-[var(--public-foreground-muted)] sm:block",
                  editorial
                    ? "text-[7.5px] tracking-[0.34em]"
                    : "text-[8.5px] tracking-[0.32em]",
                )}
              >
                {parentOrg}
              </small>
            )}
          </span>
          {" "}
          <span className="sr-only">home</span>
        </a>

        {hasNavigationItems || showSignIn ? (
          <nav
            className={cn(
              "flex shrink-0 items-center gap-2.5",
              editorial ? "sm:gap-6" : "sm:gap-[18px]",
            )}
            aria-label="Primary"
          >
            {hasNavigationItems && (
              <span
                className={cn(
                  "hidden items-center sm:flex",
                  editorial ? "gap-6 lg:gap-8" : "gap-7",
                )}
              >
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className={cn(
                      PUBLIC_LINK,
                      "font-mono uppercase text-[var(--public-foreground-soft)] transition-colors hover:text-[var(--public-foreground)]",
                      editorial
                        ? "text-[8.25px] font-semibold tracking-[0.16em]"
                        : "text-[10.5px] tracking-[0.24em]",
                    )}
                  >
                    {item.label}
                  </a>
                ))}
              </span>
            )}
            {showSignIn && (
              <Link
                to="/sign-in"
                className={`${PUBLIC_LINK} font-mono text-[10.5px] uppercase tracking-[0.24em] text-[var(--public-foreground-soft)] transition-colors hover:text-[var(--public-primary)]`}
              >
                Sign in
              </Link>
            )}
            {editorial && hasNavigationItems && (
              <button
                ref={menuTrigger}
                type="button"
                className={`${PUBLIC_FOCUS} inline-flex size-[var(--control-height)] items-center justify-center text-[var(--public-primary-deep)] transition-colors hover:text-[var(--public-primary)] sm:hidden`}
                aria-controls="mobile-primary-menu"
                aria-expanded={mobileMenuOpen}
                aria-label={
                  mobileMenuOpen ? "Close navigation" : "Open navigation"
                }
                onClick={() => setMobileMenuOpen((open) => !open)}
              >
                {mobileMenuOpen ? (
                  <X className="size-7" strokeWidth={1.6} aria-hidden="true" />
                ) : (
                  <Menu className="size-7" strokeWidth={1.6} aria-hidden="true" />
                )}
              </button>
            )}
            {primaryCta}
          </nav>
        ) : (
          <div className="flex shrink-0 items-center gap-2.5">
            {primaryCta}
          </div>
        )}
      </div>

      {editorial && hasNavigationItems && mobileMenuOpen && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
          // Negative z paints it behind this header's own background, so it
          // dims only the page below the panel. Escape is the keyboard path.
          className="fixed inset-0 -z-10 bg-[var(--public-primary-deep)]/60 sm:hidden"
        />
      )}

      {editorial && hasNavigationItems && mobileMenuOpen && (
        <nav
          id="mobile-primary-menu"
          aria-label="Mobile primary"
          className="border-t border-[var(--public-rule)] bg-[var(--public-background)] px-5 py-3 shadow-[var(--elevation-overlay)] sm:hidden"
        >
          <div className="mx-auto grid max-w-[1240px] divide-y divide-[var(--public-rule)]">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`${PUBLIC_LINK} flex min-h-[var(--control-height-large)] items-center justify-between font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--public-foreground)]`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
                <ArrowDownRight
                  className="size-4 text-[var(--public-highlight-deep)]"
                  strokeWidth={1.6}
                  aria-hidden="true"
                />
              </a>
            ))}
            <a
              href={ctaHref}
              className={`${PUBLIC_LINK} flex min-h-[var(--control-height-large)] items-center justify-between font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--public-primary)]`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {ctaLabel}
              <ArrowRight
                className="size-4 text-[var(--public-highlight-deep)]"
                strokeWidth={1.6}
                aria-hidden="true"
              />
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
